// Notifications — scheduling for every push the app sends BESIDES the
// evening recap (which lives in `eveningRecap.js`). Types covered:
//
//   1. Streak Protection   — 8:30 PM, daily
//   2. Weekly Review       — Sunday 7:00 PM
//   3. Morning Intent      — user time, default 7:00 AM, OFF by default
//   4. Brief Ready         — event-triggered (no schedule), fired by
//                            the brief job runner when a background
//                            generation completes
//   5. Quiet Hours         — master setting; if a scheduled time falls
//                            inside quiet hours, that notification is
//                            silently skipped at schedule time
//
// Architecture mirrors `eveningRecap.js`: cached prefs in module-level
// state, a pub/sub for reactive UIs, native LocalNotifications scheduling
// with daily-repeating triggers and stable IDs per type. All copy uses
// straight English (no em dashes anywhere) per app-wide style.

// LocalNotifications is iOS-only; loaded lazily so the desktop/web bundle
// doesn't try to resolve `@capacitor/local-notifications` at build time. On
// Electron/web we silently no-op every scheduling function and let the
// in-app surfaces (Dashboard, Profile) carry the UX — same as if the user
// had denied notification permission on iOS.
import { isIOS } from './platform.js';

let _localNotificationsCache;
async function loadLN() {
  if (!isIOS()) return null;
  if (_localNotificationsCache) return _localNotificationsCache;
  try {
    // Variable-indirect the specifier so Vite's dev import analyzer skips it.
    const lnMod = '@capacitor/local-notifications';
    const mod = await import(/* @vite-ignore */ lnMod);
    _localNotificationsCache = mod.LocalNotifications;
    return _localNotificationsCache;
  } catch {
    return null;
  }
}

const STORAGE_KEY = 'proxera.notif.prefs.v1';

// Notification IDs are stable per type so we can cancel + re-schedule
// without leaking old triggers. The evening-recap module owns 1001;
// everything else lives at 1002+.
const NOTIFICATION_IDS = {
  streakProtection: 1002,
  weeklyReview:     1003,
  morningIntent:    1004,
  briefReady:       1005, // event-fired only, not scheduled
};

// Sane defaults: the only daily push enabled out of the box for the
// new types is Streak Protection. Everything else is opt-in.
const DEFAULTS = {
  streakProtection: { enabled: true,  time: '20:30' },
  weeklyReview:     { enabled: true,  time: '19:00' }, // Sunday
  morningIntent:    { enabled: false, time: '07:00' },
  quietHours:       { enabled: false, start: '22:00', end: '07:00' },
};

let cachedPrefs = readFromStorage();

function readFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return cloneDefaults();
    const parsed = JSON.parse(raw);
    return mergeDefaults(parsed);
  } catch {
    return cloneDefaults();
  }
}
function cloneDefaults() { return JSON.parse(JSON.stringify(DEFAULTS)); }
function mergeDefaults(p) {
  const out = cloneDefaults();
  for (const k of Object.keys(out)) {
    if (p && typeof p[k] === 'object') {
      out[k] = { ...out[k], ...p[k] };
    }
  }
  return out;
}

// Pub/sub — Profile UI subscribes so toggles re-render reactively.
const listeners = new Set();
function emit() { for (const fn of listeners) { try { fn(); } catch {} } }
export function subscribePrefs(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getPrefs() { return cachedPrefs; }

function writePrefs(next) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  cachedPrefs = next;
  emit();
}

// Cross-tab sync.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY || e.key === null) {
      cachedPrefs = readFromStorage();
      emit();
    }
  });
}

// ── Setters ────────────────────────────────────────────────────────

export function setTypeEnabled(type, enabled) {
  const next = { ...cachedPrefs, [type]: { ...cachedPrefs[type], enabled: !!enabled } };
  writePrefs(next);
  rescheduleAll();
}

export function setTypeTime(type, time) {
  if (!/^\d{2}:\d{2}$/.test(time || '')) return;
  const next = { ...cachedPrefs, [type]: { ...cachedPrefs[type], time } };
  writePrefs(next);
  rescheduleAll();
}

export function setQuietHours({ enabled, start, end }) {
  const cur = cachedPrefs.quietHours;
  const next = {
    ...cachedPrefs,
    quietHours: {
      enabled: typeof enabled === 'boolean' ? enabled : cur.enabled,
      start: /^\d{2}:\d{2}$/.test(start || '') ? start : cur.start,
      end: /^\d{2}:\d{2}$/.test(end || '') ? end : cur.end,
    },
  };
  writePrefs(next);
  rescheduleAll();
}

// ── Time helpers ───────────────────────────────────────────────────

export function parseTime(s) {
  const m = /^(\d{2}):(\d{2})$/.exec(s || '');
  if (!m) return { hours: 0, minutes: 0 };
  return { hours: clamp(+m[1], 0, 23), minutes: clamp(+m[2], 0, 59) };
}
function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

export function formatTime12(s) {
  const { hours, minutes } = parseTime(s);
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h12 = ((hours % 12) || 12);
  return `${h12}:${String(minutes).padStart(2, '0')} ${ampm}`;
}

// Is HH:MM inside the quiet-hours window? Handles overnight ranges
// (e.g. 22:00 to 07:00) by treating the window as a wrap-around set.
function isInQuietHours(time, quiet) {
  if (!quiet?.enabled) return false;
  const t = toMinutes(time);
  const s = toMinutes(quiet.start);
  const e = toMinutes(quiet.end);
  if (s === e) return false;
  if (s < e) return t >= s && t < e;          // same-day window
  return t >= s || t < e;                      // overnight wrap
}
function toMinutes(s) {
  const { hours, minutes } = parseTime(s);
  return hours * 60 + minutes;
}

// ── Native scheduling ──────────────────────────────────────────────

export async function ensurePermission() {
  const LN = await loadLN();
  if (!LN) return false;
  try {
    const status = await LN.checkPermissions();
    if (status.display === 'granted') return true;
    const req = await LN.requestPermissions();
    return req.display === 'granted';
  } catch {
    return false;
  }
}

// Compute the next firing date for a daily/weekly schedule. `weekday`,
// when provided, restricts to that day-of-week (0 = Sunday). The first
// fire is computed from "now" so a same-day setting that's already past
// rolls forward.
function nextFireDate({ time, weekday }) {
  const { hours, minutes } = parseTime(time);
  const next = new Date();
  next.setHours(hours, minutes, 0, 0);
  if (typeof weekday === 'number') {
    while (next <= new Date() || next.getDay() !== weekday) {
      next.setDate(next.getDate() + 1);
    }
  } else if (next <= new Date()) {
    next.setDate(next.getDate() + 1);
  }
  return next;
}

// Tear down + re-schedule every type. Called on prefs changes and on
// boot so the OS-side schedule always matches what's persisted.
export async function rescheduleAll() {
  const LN = await loadLN();
  if (!LN) return;

  // Cancel everything first so a disable cleanly leaves no orphan.
  try {
    await LN.cancel({
      notifications: Object.values(NOTIFICATION_IDS).map(id => ({ id })),
    });
  } catch { /* nothing scheduled */ }

  const prefs = getPrefs();

  // Permission is required before any schedule call. Skip silently if
  // denied; the in-app surfaces still work.
  const granted = await ensurePermission();
  if (!granted) return;

  const toSchedule = [];

  // 1. Streak Protection — 8:30 PM daily. Generic copy because we
  //    cannot personalize a pre-scheduled push with runtime streak
  //    data (the OS holds the schedule). Tapping deep-links into the
  //    Dashboard where their actual streak state shows.
  if (prefs.streakProtection.enabled
      && !isInQuietHours(prefs.streakProtection.time, prefs.quietHours)) {
    toSchedule.push({
      id: NOTIFICATION_IDS.streakProtection,
      title: 'Don’t lose your streak',
      body: 'Tap to check in before midnight.',
      schedule: {
        at: nextFireDate({ time: prefs.streakProtection.time }),
        repeats: true,
        every: 'day',
        allowWhileIdle: true,
      },
    });
  }

  // 2. Weekly Review — Sundays at 7:00 PM. Sunday = weekday 0.
  if (prefs.weeklyReview.enabled
      && !isInQuietHours(prefs.weeklyReview.time, prefs.quietHours)) {
    toSchedule.push({
      id: NOTIFICATION_IDS.weeklyReview,
      title: 'Your week, in one view',
      body: 'Recap how the week landed and set next week’s goals.',
      schedule: {
        at: nextFireDate({ time: prefs.weeklyReview.time, weekday: 0 }),
        repeats: true,
        every: 'week',
        allowWhileIdle: true,
      },
    });
  }

  // 3. Morning Intent — opt-in. Quiet hours can knock it out if the
  //    user picked a time inside that window.
  if (prefs.morningIntent.enabled
      && !isInQuietHours(prefs.morningIntent.time, prefs.quietHours)) {
    toSchedule.push({
      id: NOTIFICATION_IDS.morningIntent,
      title: 'Good morning',
      body: 'Pick a habit to start the day with.',
      schedule: {
        at: nextFireDate({ time: prefs.morningIntent.time }),
        repeats: true,
        every: 'day',
        allowWhileIdle: true,
      },
    });
  }

  if (toSchedule.length === 0) return;

  try {
    await LN.schedule({ notifications: toSchedule });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[notifications] schedule failed', err);
  }
}

// Brief Ready — event-triggered. Called by the brief job runner the
// moment a background generation completes. We fire immediately
// (5-second delayed schedule, the smallest practical "now") rather
// than scheduling a calendar trigger.
export async function fireBriefReady({ bundleName, insightCount }) {
  const LN = await loadLN();
  if (!LN) return;
  const prefs = getPrefs();
  // Respect quiet hours for the immediate fire too. Compute the
  // current local clock as HH:MM and gate.
  const now = new Date();
  const nowStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  if (isInQuietHours(nowStr, prefs.quietHours)) return;

  const granted = await ensurePermission();
  if (!granted) return;

  try {
    await LN.schedule({
      notifications: [{
        id: NOTIFICATION_IDS.briefReady,
        title: 'Today’s brief is ready',
        body: bundleName
          ? `${bundleName} brief generated. ${insightCount || 8} insights, ready when you are.`
          : 'Your daily brief just finished generating.',
        schedule: {
          at: new Date(Date.now() + 3 * 1000),
          allowWhileIdle: true,
        },
      }],
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[notifications] brief-ready fire failed', err);
  }
}

// Boot-time sync — App.jsx calls this once after mount.
export async function bootSync() {
  await rescheduleAll();
}
